/**
 * Ajustes del calendario POR CATEGORÍA (Eduardo 2026-10-01, torneo 59:
 * «los bloques de minutos son por categoría, cada que cambie de categoría se
 * debe guardar su bloque de minutos y su número de canchas»).
 *
 * Cada categoría guarda en el servidor su Bloque min, #Canchas, Desde y
 * Último inicio (`calendar_settings_json` de la división). La duración de
 * cada partido en el grid es la de SU categoría: un partido de una categoría
 * de 60 min dura 60 aunque estés viendo una de 75 (antes todos se pintaban
 * con el bloque de la categoría abierta y salían carriles encimados falsos).
 */

import type { DivisionCalendarSettings, TournamentDivision, TournamentMatch } from "./api";
import { cleanSplitParts, isSplittableBase, type SplitParts } from "./courts";

/** Lo que guarda una categoría (todo opcional; mismo tipo que manda el servidor). */
export type { DivisionCalendarSettings };

/** Valores de los campos del calendario tal como viven en los inputs. */
export interface CalendarFieldValues {
  startTime: string;
  endTime: string;
  blockMinutes: string;
  courtCount: string;
}

/** Los de siempre cuando nada se ha guardado en el torneo. */
export const DEFAULT_CALENDAR_FIELDS: CalendarFieldValues = {
  startTime: "08:00",
  endTime: "18:00",
  blockMinutes: "60",
  courtCount: "3",
};

// Mismos rangos que valida el servidor.
const MIN_DURATION = 10;
const MAX_DURATION = 600;
const MIN_COURTS = 1;
const MAX_COURTS = 50;

function integerInRange(value: unknown, min: number, max: number): number | null {
  const number = typeof value === "string" && value.trim() !== "" ? Number(value) : value;
  if (typeof number !== "number" || !Number.isInteger(number)) return null;
  return number >= min && number <= max ? number : null;
}

/** Duración válida (10–600 min) o null. */
export function validDurationMinutes(value: unknown): number | null {
  return integerInRange(value, MIN_DURATION, MAX_DURATION);
}

/** #Canchas válido (1–50) o null. */
export function validCourtCount(value: unknown): number | null {
  return integerInRange(value, MIN_COURTS, MAX_COURTS);
}

/** "HH:MM" válido (acepta "HH:MM:SS" del servidor y lo recorta) o null. */
export function validClockTime(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const match = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(value.trim());
  if (!match || Number(match[1]) > 23 || Number(match[2]) > 59) return null;
  return `${match[1]}:${match[2]}`;
}

/** Deja solo lo válido: un dato raro nunca rompe el calendario (ese campo
 * usa el valor que ya estaba en pantalla). */
export function sanitizeCalendarSettings(raw: unknown): DivisionCalendarSettings {
  if (!raw || typeof raw !== "object") return {};
  const source = raw as Record<string, unknown>;
  const settings: DivisionCalendarSettings = {};
  const duration = validDurationMinutes(source.match_duration_minutes);
  if (duration !== null) settings.match_duration_minutes = duration;
  const courts = validCourtCount(source.court_count);
  if (courts !== null) settings.court_count = courts;
  const start = validClockTime(source.start_time);
  if (start !== null) settings.start_time = start;
  const end = validClockTime(source.end_time);
  if (end !== null) settings.end_time = end;
  return settings;
}

export function hasCalendarSettings(settings: DivisionCalendarSettings | null | undefined): boolean {
  if (!settings) return false;
  return (
    settings.match_duration_minutes != null ||
    settings.court_count != null ||
    settings.start_time != null ||
    settings.end_time != null
  );
}

/** Ajustes guardados de una categoría (`calendar_settings_json` del servidor). */
export function divisionCalendarSettings(
  division: Pick<TournamentDivision, "calendar_settings_json"> | null | undefined,
): DivisionCalendarSettings {
  if (!division) return {};
  return sanitizeCalendarSettings(division.calendar_settings_json ?? null);
}

/** Campos a partir de lo guardado; lo que la categoría no tiene guardado se
 * queda con `fallback` (lo que ya estaba en pantalla). */
export function fieldsFromSettings(
  settings: DivisionCalendarSettings,
  fallback: CalendarFieldValues,
): CalendarFieldValues {
  return {
    startTime: settings.start_time ?? fallback.startTime,
    endTime: settings.end_time ?? fallback.endTime,
    blockMinutes: settings.match_duration_minutes != null ? String(settings.match_duration_minutes) : fallback.blockMinutes,
    courtCount: settings.court_count != null ? String(settings.court_count) : fallback.courtCount,
  };
}

/** Lo que se manda al servidor: siempre los cuatro campos. Un campo a medio
 * escribir (p. ej. #Canchas vacío) conserva lo último guardado; si nunca se
 * guardó, se omite. */
export function settingsFromFields(
  fields: CalendarFieldValues,
  previous: DivisionCalendarSettings = {},
): DivisionCalendarSettings {
  const settings: DivisionCalendarSettings = {};
  const duration = validDurationMinutes(fields.blockMinutes) ?? previous.match_duration_minutes ?? null;
  if (duration != null) settings.match_duration_minutes = duration;
  const courts = validCourtCount(fields.courtCount) ?? previous.court_count ?? null;
  if (courts != null) settings.court_count = courts;
  const start = validClockTime(fields.startTime) ?? previous.start_time ?? null;
  if (start != null) settings.start_time = start;
  const end = validClockTime(fields.endTime) ?? previous.end_time ?? null;
  if (end != null) settings.end_time = end;
  return settings;
}

// Lo que acepta el campo "Bloque min" (min 15, max 240).
const MIN_BLOCK_FIELD = 15;
const MAX_BLOCK_FIELD = 240;

/** Bloque min que se puede GUARDAR desde el campo (15–240, lo que el campo
 * acepta) o null. Así un "12" a medio escribir "120" nunca se guarda. */
export function validBlockFieldMinutes(value: unknown): number | null {
  return integerInRange(value, MIN_BLOCK_FIELD, MAX_BLOCK_FIELD);
}

/** Lo que se guarda para una categoría desde los campos (revisión 2026-10-01):
 * como `settingsFromFields`, pero el Bloque min solo va si `includeBlock`
 * (es de ESTA categoría: ya lo tenía guardado o el admin lo cambió o lo
 * confirmó en ella) y si es un valor que el campo acepta. Un bloque heredado
 * de otra categoría nunca se guarda solo: le cambiaría la duración a los
 * partidos que esta categoría ya tiene programados. Sin bloque, la llave no
 * va y el servidor conserva lo que tenga. */
export function calendarSettingsToSave(
  fields: CalendarFieldValues,
  previous: DivisionCalendarSettings,
  includeBlock: boolean,
): DivisionCalendarSettings {
  const settings = settingsFromFields(fields, previous);
  const block = includeBlock ? validBlockFieldMinutes(fields.blockMinutes) : null;
  const duration = block ?? previous.match_duration_minutes ?? null;
  if (duration != null) settings.match_duration_minutes = duration;
  else delete settings.match_duration_minutes;
  return settings;
}

export function sameCalendarSettings(a: DivisionCalendarSettings, b: DivisionCalendarSettings): boolean {
  return (
    (a.match_duration_minutes ?? null) === (b.match_duration_minutes ?? null) &&
    (a.court_count ?? null) === (b.court_count ?? null) &&
    (a.start_time ?? null) === (b.start_time ?? null) &&
    (a.end_time ?? null) === (b.end_time ?? null)
  );
}

/** Al abrir una categoría que aún no tiene nada guardado: los últimos
 * valores usados en el torneo (Eduardo: «si en una categoría el admin
 * seleccionó 7 o 10 canchas, que en la siguiente ya aparezcan»). Sin nada
 * en memoria se toma la categoría guardada más recientemente. */
export function latestDivisionCalendarSettings(
  divisions: Array<Pick<TournamentDivision, "id" | "name" | "updated_at" | "calendar_settings_json">>,
  overrides: Record<number, DivisionCalendarSettings> = {},
  excludeDivisionId: number | null = null,
): { divisionId: number; name: string; settings: DivisionCalendarSettings } | null {
  let best: { divisionId: number; name: string; settings: DivisionCalendarSettings; stamp: number } | null = null;
  for (const division of divisions) {
    if (division.id === excludeDivisionId) continue;
    const settings = overrides[division.id] ?? divisionCalendarSettings(division);
    if (!hasCalendarSettings(settings)) continue;
    // Lo guardado en esta pestaña es lo más reciente.
    const parsed = division.updated_at ? Date.parse(division.updated_at) : Number.NaN;
    const stamp = overrides[division.id] ? Number.POSITIVE_INFINITY : Number.isFinite(parsed) ? parsed : 0;
    if (!best || stamp > best.stamp) best = { divisionId: division.id, name: division.name, settings, stamp };
  }
  return best ? { divisionId: best.divisionId, name: best.name, settings: best.settings } : null;
}

// ---- Mini canchas (Eduardo 2026-10-01) --------------------------------------
// «Tipo de cancha» (Formato) y las canchas divididas (casillas del calendario)
// viven en el MISMO calendar_settings_json, pero NO pasan por las funciones de
// cuatro campos de arriba: el guardado automático de Bloque min / #Canchas
// nunca las manda ni las borra (el servidor junta lo que no se manda) y una
// categoría nueva nunca hereda la división de otra.

export type DivisionCourtType = "normal" | "mini";

export interface DivisionCourtSetup {
  courtType: DivisionCourtType;
  /** Canchas ("1".."50") divididas en mini canchas en esa sede. */
  splitBases: Set<string>;
  /** En cuántas mini canchas se parte cada cancha dividida (solo 3 o 4;
   * sin entrada = 2, A y B: lo guardado antes de 2026-10-08). */
  splitParts: Map<string, SplitParts>;
}

/** Base "9" limpia o null (acepta 9 y "09"; descarta "9-A", "0", "x"…). */
function cleanSplitBase(value: unknown): string | null {
  const text = typeof value === "number" ? String(value) : typeof value === "string" ? value.trim() : "";
  if (!/^\d{1,2}$/.test(text)) return null;
  const base = String(Number(text));
  return isSplittableBase(base) ? base : null;
}

/** Tipo de cancha de una categoría y sus canchas divididas en la sede
 * `venue` ("" = principal; las demás con venueKey). Defensivo: un dato raro
 * nunca rompe el calendario. */
export function divisionCourtSetup(
  division: Pick<TournamentDivision, "calendar_settings_json"> | null | undefined,
  venue: string = "",
): DivisionCourtSetup {
  const raw: unknown = division?.calendar_settings_json ?? null;
  const setup: DivisionCourtSetup = { courtType: "normal", splitBases: new Set(), splitParts: new Map() };
  if (!raw || typeof raw !== "object") return setup;
  const source = raw as Record<string, unknown>;
  if (source.court_type === "mini") setup.courtType = "mini";
  const map = source.split_courts;
  if (map && typeof map === "object" && !Array.isArray(map)) {
    const list = (map as Record<string, unknown>)[venue];
    if (Array.isArray(list)) {
      for (const value of list) {
        const base = cleanSplitBase(value);
        if (base) setup.splitBases.add(base);
      }
    }
  }
  const partsMap = source.split_parts;
  if (partsMap && typeof partsMap === "object" && !Array.isArray(partsMap)) {
    const entries = (partsMap as Record<string, unknown>)[venue];
    if (entries && typeof entries === "object" && !Array.isArray(entries)) {
      for (const [rawBase, rawParts] of Object.entries(entries as Record<string, unknown>)) {
        const base = cleanSplitBase(rawBase);
        const parts = cleanSplitParts(rawParts);
        if (base && setup.splitBases.has(base) && parts !== 2) setup.splitParts.set(base, parts);
      }
    }
  }
  return setup;
}

/** Bases ordenadas por número ("10" después de "9"). */
export function sortedSplitBases(bases: Iterable<string>): string[] {
  return [...new Set(bases)].sort((a, b) => Number(a) - Number(b) || a.localeCompare(b));
}

/** Minutos que ocupa un partido en el grid:
 * 1. de la categoría ABIERTA → el bloque en pantalla (lo que se está
 *    editando; se guarda para ella);
 * 2. de otra categoría del torneo con ajuste guardado en esta pestaña;
 * 3. la duración que manda el servidor (`duration_minutes`, la de su
 *    categoría — también la de otros torneos del club);
 * 4. el ajuste guardado de su categoría en la lista de categorías;
 * 5. sin nada de lo anterior, `fallbackMinutes` (por omisión el bloque en
 *    pantalla). Con un partido elegido, el calendario pasa aquí lo que dura
 *    ESE partido: es lo que el servidor asume para un partido sin bloque
 *    guardado al revisar choques (revisión 2026-10-01), así el grid y el
 *    servidor dicen lo mismo. */
export function matchDurationForGrid(
  match: Pick<TournamentMatch, "tournament_id" | "division_id" | "duration_minutes">,
  context: {
    tournamentId: number;
    currentDivisionId: number | null;
    currentBlockMinutes: number;
    savedDurationByDivision: Map<number, number>;
    overrideDurationByDivision?: Map<number, number>;
    fallbackMinutes?: number;
  },
): number {
  const sameTournament = match.tournament_id === context.tournamentId;
  if (sameTournament && context.currentDivisionId !== null && match.division_id === context.currentDivisionId) {
    return context.currentBlockMinutes;
  }
  if (sameTournament && match.division_id != null) {
    const override = context.overrideDurationByDivision?.get(match.division_id);
    if (override) return override;
  }
  const fromServer = validDurationMinutes(match.duration_minutes);
  if (fromServer !== null) return fromServer;
  if (sameTournament && match.division_id != null) {
    const saved = context.savedDurationByDivision.get(match.division_id);
    if (saved) return saved;
  }
  return context.fallbackMinutes ?? context.currentBlockMinutes;
}
